import { createFileRoute, Link } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { HubEditorial } from "@/components/site/HubEditorial";
import { HUB_EDITORIAL } from "@/data/hub-editorial";
import { socialImageMeta } from "@/lib/social";
import { ShareBar } from "@/components/site/ShareBar";
import { ArticleDisclaimer } from "@/components/site/ArticleDisclaimer";
import { makeSeoDescription } from "@/lib/seo-description";

const CANONICAL = "https://lifewestway.com/healthcare";
const TITLE = "Health Care Guides for Expats 2026 | LifeWestWay";

export const Route = createFileRoute("/healthcare")({
  head: () => ({
    meta: [
      { title: TITLE },
      {
        name: "description",
        content: makeSeoDescription(
          "Health care guides for USA, UK, Australia, Canada, Italy & Portugal (2026) — how to register for public cover, what insurance costs, waiting periods and what newcomers actually pay."
        ),
      },
      { property: "og:title", content: TITLE },
      { property: "og:site_name", content: "LifeWestWay" },
      {
        property: "og:description",
        content: makeSeoDescription(
          "Six countries, six 2026 health care playbooks — NHS, Medicare, provincial health cards, SSN, SNS and US insurance explained for new arrivals."
        ),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      ...socialImageMeta(),
      { name: "twitter:title", content: TITLE },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
  }),
  component: HealthcareHub,
});

type Guide = {
  country: string;
  flag: string;
  accent: string;
  title: string;
  blurb: string;
  highlight: string;
  to: string;
  params: Record<string, string>;
};

const GUIDES: Guide[] = [
  {
    country: "United States",
    flag: "🇺🇸",
    accent: "from-sky-500/25 to-indigo-400/10",
    title: "How US Health Care Works for Immigrants (2026)",
    blurb:
      "Employer plans, ACA marketplace cover, deductibles and out-of-pocket maximums — how to be insured from week one and avoid five-figure bills.",
    highlight: "No public system — insurance first",
    to: "/usa/$slug",
    params: { slug: "how-healthcare-works-usa-immigrants-2026" },
  },
  {
    country: "United Kingdom",
    flag: "🇬🇧",
    accent: "from-indigo-500/25 to-sky-400/10",
    title: "How to Register With an NHS GP (2026)",
    blurb:
      "The Immigration Health Surcharge, GP registration without proof of address, NHS numbers, prescriptions and when to use 111 instead of A&E.",
    highlight: "NHS access from visa start date",
    to: "/uk/$slug",
    params: { slug: "how-to-register-with-a-gp-uk-nhs-2026" },
  },
  {
    country: "Australia",
    flag: "🇦🇺",
    accent: "from-amber-500/25 to-emerald-400/10",
    title: "How Medicare Works for Immigrants (2026)",
    blurb:
      "Medicare eligibility by visa subclass, reciprocal agreements, bulk billing, the Medicare Levy and where OSHC fills the gap for students.",
    highlight: "Medicare card + OSHC rules",
    to: "/australia/$slug",
    params: { slug: "how-medicare-works-australia-immigrants-2026" },
  },
  {
    country: "Canada",
    flag: "🇨🇦",
    accent: "from-red-500/25 to-rose-400/10",
    title: "How to Get a Provincial Health Card (2026)",
    blurb:
      "Province-by-province enrolment, the up-to-three-month waiting period, what OHIP/MSP/RAMQ cover and the private top-up most newcomers still need.",
    highlight: "Cover the 3-month waiting gap",
    to: "/canada/$slug",
    params: { slug: "how-to-get-health-card-canada-2026" },
  },
  {
    country: "Italy",
    flag: "🇮🇹",
    accent: "from-emerald-500/25 to-rose-500/10",
    title: "How to Get the Tessera Sanitaria (2026)",
    blurb:
      "SSN registration at your local ASL, the voluntary annual contribution, choosing a medico di base and what public cover leaves out.",
    highlight: "SSN enrolment via your local ASL",
    to: "/italy/$slug",
    params: { slug: "how-to-get-tessera-sanitaria-italy-2026" },
  },
  {
    country: "Portugal",
    flag: "🇵🇹",
    accent: "from-emerald-500/25 to-amber-400/10",
    title: "How to Register With the SNS (2026)",
    blurb:
      "Getting a número de utente at your Centro de Saúde, user fees, waiting times and why most residents add a private plan for specialists.",
    highlight: "Número de utente + private top-up",
    to: "/portugal/$slug",
    params: { slug: "how-to-register-with-sns-portugal-2026" },
  },
];

function HealthcareHub() {
  return (
    <main id="main-content" className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Health Care" }]} />

      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl border border-border/60 bg-gradient-to-br from-card/80 via-card/40 to-background/60 p-8 sm:p-12">
        <span
          aria-hidden
          className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-accent/20 blur-3xl"
        />
        <span
          aria-hidden
          className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-accent/10 blur-3xl"
        />
        <div className="relative">
          <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">
            <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" />
            6 Countries · Updated 2026
          </span>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Health Care &amp; <span className="text-accent">Insurance Hub</span>
          </h1>
          <p className="mt-4 max-w-2xl text-base sm:text-lg text-foreground/75">
            How to get covered in the USA, UK, Australia, Canada, Italy and Portugal —
            public enrolment steps, waiting periods, what insurance actually costs and the
            bills newcomers never see coming.
          </p>
        </div>
      </section>

      {/* Grid */}
      <section
        aria-label="Health care guides by country"
        className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3"
      >
        {GUIDES.map((g) => (
          <Link title={`Read the healthcare guide: ${g.title}`}
            key={g.title}
            to={g.to}
            params={g.params}
            className="group relative flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-card/50 p-6 transition-all hover:border-accent/60 hover:bg-card/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <span
              aria-hidden
              className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${g.accent} opacity-0 transition-opacity group-hover:opacity-100`}
            />
            <div className="relative flex items-center justify-between">
              <span className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/40 px-3 py-1 text-xs font-semibold text-foreground/80">
                <span aria-hidden className="text-base leading-none">
                  {g.flag}
                </span>
                {g.country}
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent/90">
                2026
              </span>
            </div>

            <h2 className="relative mt-4 font-display text-xl font-bold tracking-tight text-foreground group-hover:text-accent">
              {g.title}
            </h2>
            <p className="relative mt-3 text-sm text-foreground/70">{g.blurb}</p>

            <div className="relative mt-5 flex items-center justify-between border-t border-border/50 pt-4">
              <span className="text-xs font-medium text-foreground/60">
                <span className="text-accent">◆</span> {g.highlight}
              </span>
              <span className="text-sm font-semibold text-accent">
                Read guide <span aria-hidden>→</span>
              </span>
            </div>
          </Link>
        ))}
      </section>

      <HubEditorial data={HUB_EDITORIAL.healthcare} />

      <ArticleDisclaimer />

      <ShareBar />
    </main>
  );
}
